import React, { useEffect, useState } from 'react';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { IoSearch } from 'react-icons/io5';
import { toast } from 'react-toastify';
import AddZoneModal from './AddZoneModal';
import EditZoneModal from './EditZoneModal';
import DeleteZoneModal from './DeleteZoneModal';
import Pagination from 'UpdatedComponents/Pagination';
import { getZones, restoreZone } from 'UpdatedFeatures/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Puff } from 'react-loader-spinner';
import TrashedSwitch from 'UpdatedComponents/TrashedSwitch';
import { MdOutlineRestorePage } from 'react-icons/md';
import RestoreModal from 'UpdatedFeatures/RestoreModal';

const ZoneProTable = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [isArchived, setIsArchived] = useState(false);
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [totalPages, setTotalPages] = useState(0)
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const queryClient = useQueryClient();

  const restoreMutation = useMutation({
    mutationFn: restoreZone,
    onSuccess: () => {
      queryClient.invalidateQueries(['subscriptions']);
      toast.success("Zone restauré avec succès");
      setRestoreModalOpen(false);
    },
    onError: (error) => {
      toast.error(error.message || "Erreur lors de la restauration");
    }
  });

  const handleRestoreConfirm = () => {
    if (selectedZone) {
      restoreMutation.mutate(selectedZone.id);
    }
  };
  const { data: zonesData, isLoading, isError } = useQuery({
    queryKey: ["zones", page, perPage, search, isArchived],
    queryFn: () => getZones(page, perPage, search, isArchived),
  });
  useEffect(() => {
    if (zonesData?.data) {
      setZones(zonesData.data);
      setTotalPages(Math.ceil(zonesData.total / perPage));
    }
  }, [zonesData]);


  const handleRestoreClick = (zone) => {
    setSelectedZone(zone);
    setRestoreModalOpen(true);
  };
  console.log(zonesData)

  const handleAddZone = (newZone) => {
    setZones([...zones, { ...newZone, id: zones.length + 1 }]);
    setShowAddModal(false);
    toast.success('Zone ajoutée avec succès');
  };

  const handleEditZone = (updatedZone) => {
    setZones(zones.map(z => z.id === updatedZone.id ? updatedZone : z));
    setShowEditModal(false);
    toast.success('Zone mise à jour avec succès');
  };

  const handleDeleteZone = (zoneId) => {
    setZones(zones.filter(z => z.id !== zoneId));
    setShowDeleteModal(false);
    toast.success('Zone supprimée avec succès');
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-12">
      <div className="flex justify-between items-center mb-4">
        <div className="relative w-1/3">
          <input
            type="text"
            placeholder="Rechercher une zone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full p-2 border border-[#b269ae] rounded-md shadow-sm pl-10"
          />
          <IoSearch className="absolute left-3 top-3 text-[#b269ae]" />
        </div>
        <div className="flex items-center gap-5">
          <div>
            <TrashedSwitch enabled={isArchived} setEnabled={setIsArchived} />
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-[#b269ae] text-white px-4 py-2 rounded hover:bg-[#9c58a3]"
          >
            + Ajouter une zone
          </button>
        </div>
      </div>
      {isLoading ?
        <div className="flex items-center justify-center min-h-[400px]">
          <Puff visible={true} height="80" width="80" color="#895a7c" ariaLabel="puff-loading" />
        </div> : (
          <div className="overflow-x-auto shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-[#b269ae] text-white">
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-medium">Id</th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Pays</th>
                  <th className="px-6 py-3 text-left text-sm font-medium">Nombres d'Entrepots</th>
                  <th className="px-6 py-3 text-right text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {zones?.map(zone => (
                  <tr key={zone?.id}>
                    <td className="px-6 py-4 text-sm text-gray-700">{zone?.id}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 capitalize">{zone?.country}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{zone?.locations?.length ?? 0}</td>

                    <td className="px-6 py-4 text-right text-sm font-medium flex gap-3 justify-end">
                      {isArchived ? (
                        <button
                          className="text-[#b269ae] hover:text-purple-300"
                          onClick={() => handleRestoreClick(zone)}
                        >
                          <MdOutlineRestorePage size={25} />
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              setSelectedZone(zone);
                              setShowEditModal(true);
                            }}
                            className="text-[#b269ae] hover:text-[#e18ddd]"
                            title="Modifier"
                          >
                            <FaEdit size={17} />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedZone(zone);
                              setShowDeleteModal(true);
                            }}
                            className="text-[#b269ae] hover:text-[#eb9ae7]"
                            title="Supprimer"
                          >
                            <FaTrash size={17} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {zones.length === 0 && (
              <div className="p-4 text-center text-gray-500">Aucune zone trouvée.</div>
            )}
          </div>)}

      <Pagination
        prevPage={() => setPage(prev => Math.max(prev - 1, 1))}
        nextPage={() => setPage(prev => Math.min(prev + 1, totalPages))}
        totalPages={totalPages}
        setPage={setPage}
      />

      <AddZoneModal
        open={showAddModal}
        setOpen={() => setShowAddModal(false)}
        onSave={handleAddZone}
      />
      <EditZoneModal
        open={showEditModal}
        setOpen={() => setShowEditModal(false)}
        zone={selectedZone}
        onSave={handleEditZone}
      />
      <DeleteZoneModal
        open={showDeleteModal}
        setOpen={() => setShowDeleteModal(false)}
        zone={selectedZone}
        onDelete={() => handleDeleteZone(selectedZone.id)}
      />
      <RestoreModal
        open={restoreModalOpen}
        onConfirm={handleRestoreConfirm}
        onCancel={() => setRestoreModalOpen(false)}
        isLoading={restoreMutation.isPending}
        itemTitle={"Zone"}
      />
    </div>
  );
};

export default ZoneProTable;
